/* eslint-disable no-underscore-dangle */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Grid, DialogContent } from '@material-ui/core';

import api from '../../services/api';
import PDF from '../PDF';
import ModalProduct from '../ModalProduct';
import ModalCart from '../ModalCart';
import useViewAsContext from '../../hooks/useViewAsContext';
import useUserContext from '../../hooks/useUserContext';
import useConfigContext from '../../hooks/useConfigContext';
import { events } from '../../consts';
import { useEventDispatcher } from '../../hooks';

import { StyledDialog, Transition } from './styles';
import {
  productsForDeliveryAndCollection,
  groupByCapsula,
  groupProductsForPDF,
  getAdjacentProduct,
} from './utils';
import Header from './components/Header';
import CollectionTitle from './components/CollectionTitle';
import StyleCollectionTabs from './components/StyleCollectionTabs';
import Toolbar from './components/Toolbar';
import CapsulaSection from './components/CapsulaSection';

export default function ModalCollection({ open, handleClose }) {
  const { configState } = useConfigContext();
  const { deliveries } = configState;
  const seasonName = configState.season.name || '';

  const { userState } = useUserContext();
  const { user: profile } = userState;

  const { viewAsState } = useViewAsContext();
  const { selectedUser } = viewAsState;

  const dispatchEvent = useEventDispatcher();

  const [allProducts, setAllProducts] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(1);
  const [styleCollection, setStyleCollection] = useState('MAIN');
  const [selectedProduct, setSelectedProduct] = useState(1);
  const [openModalProduct, setOpenModalProduct] = useState(false);
  const [openCart, setOpenCart] = useState(false);
  const [print, setPrint] = useState(false);

  const componentRef = useRef();
  const selectedUserId = selectedUser?._id || null;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/collections', {
          params: {
            delivery_id: null,
            categories: null,
            user_id: selectedUserId,
          },
        });
        if (!cancelled) setAllProducts(response.data || []);
      } catch (err) {
        if (!cancelled) setAllProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedUserId]);

  const myProducts = useMemo(
    () =>
      productsForDeliveryAndCollection(
        allProducts,
        selectedDelivery,
        styleCollection
      ),
    [allProducts, selectedDelivery, styleCollection]
  );

  const capsulaSections = useMemo(() => groupByCapsula(myProducts), [
    myProducts,
  ]);

  const pdfGroups = useMemo(() => groupProductsForPDF(allProducts), [
    allProducts,
  ]);

  const deliverySubTitle = useMemo(() => {
    const current = deliveries?.[selectedDelivery - 1];
    if (!current) return '';
    return `${current.subTitle1 || ''} | ${current.subTitle2 || ''}`;
  }, [deliveries, selectedDelivery]);

  const handleRemoveProduct = useCallback(
    async product => {
      try {
        await api.post('/collections', {
          style_code: product.style_code,
          user_id: selectedUserId,
        });
        setAllProducts(prev =>
          prev.filter(p => p.style_code !== product.style_code)
        );
      } catch (err) {
        // ignore: the product stays visible until the next refresh
      }
    },
    [selectedUserId]
  );

  const handleSelectProduct = useCallback(product => {
    setSelectedProduct(product);
    setOpenModalProduct(true);
  }, []);

  const handlePrevProduct = useCallback(() => {
    setSelectedProduct(prev => getAdjacentProduct(myProducts, prev, 'prev'));
  }, [myProducts]);

  const handleNextProduct = useCallback(() => {
    setSelectedProduct(prev => getAdjacentProduct(myProducts, prev, 'next'));
  }, [myProducts]);

  const handleStyleCollectionChange = useCallback(key => {
    setStyleCollection(key);
    setSelectedDelivery(1);
  }, []);

  const handleStartNotes = useCallback(() => {
    dispatchEvent(events.startNotes);
    setOpenCart(true);
  }, [dispatchEvent]);

  const handleCloseCart = useCallback(() => setOpenCart(false), []);
  const handleCloseProduct = useCallback(() => setOpenModalProduct(false), []);
  const handleBeforePrint = useCallback(() => setPrint(true), []);
  const handleAfterPrint = useCallback(() => setPrint(false), []);

  return (
    <div>
      <StyledDialog
        open={open}
        TransitionComponent={Transition}
        onClose={handleClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        fullScreen
      >
        <DialogContent
          style={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <ModalCart open={openCart} handleClose={handleCloseCart} />
          {print && (
            <Grid container ref={componentRef}>
              <PDF name={profile?.name} {...pdfGroups} />
            </Grid>
          )}
          {!print && (
            <Grid style={{ width: '100%', height: '100%' }} id="modal">
              <Grid
                container
                justifyContent="space-between"
                alignItems="space-around"
                style={{ margin: '10px 0px 0px 20px' }}
                direction="column"
              >
                <Header onClose={handleClose} />
                <CollectionTitle
                  profile={profile}
                  selectedUser={selectedUser}
                  seasonName={seasonName}
                />
                <StyleCollectionTabs
                  value={styleCollection}
                  onChange={handleStyleCollectionChange}
                />
                <Toolbar
                  selectedDelivery={selectedDelivery}
                  onSelectDelivery={setSelectedDelivery}
                  styleCollection={styleCollection}
                  hasProducts={allProducts.length > 0}
                  onStartNotes={handleStartNotes}
                  printRef={componentRef}
                  onBeforePrint={handleBeforePrint}
                  onAfterPrint={handleAfterPrint}
                />
              </Grid>

              {capsulaSections.map(({ capsula, products }) => (
                <CapsulaSection
                  key={capsula}
                  capsula={capsula}
                  products={products}
                  deliverySubTitle={deliverySubTitle}
                  print={print}
                  onSelectProduct={handleSelectProduct}
                  onRemoveProduct={handleRemoveProduct}
                />
              ))}
            </Grid>
          )}
          <ModalProduct
            products={myProducts}
            handlePrevProduct={handlePrevProduct}
            handleNextProduct={handleNextProduct}
            open={openModalProduct}
            handleClose={handleCloseProduct}
            style_code={selectedProduct}
          />
        </DialogContent>
      </StyledDialog>
    </div>
  );
}
